import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * 제네릭 선택 상태 인터페이스
 * @template T - 선택 가능한 아이템의 타입
 */
export interface SelectionState<T> {
  /** 현재 선택된 아이템 배열 */
  selectedItems: T[];
  /** 아이템 추가 */
  addItem: (item: T) => void;
  /** 아이템 제거 */
  removeItem: (item: T) => void;
  /** 모든 아이템 추가 */
  addAllItems: () => void;
  /** 모든 아이템 제거 */
  removeAllItems: () => void;
  /** 아이템 토글 (선택/해제) */
  toggleItem: (item: T) => void;
  /** 모든 아이템이 선택되었는지 여부 */
  isAllSelected: boolean;
  /** 선택 상태 초기화 */
  reset: () => void;
}

/**
 * 제네릭 선택 상태 관리 훅
 *
 * 컨테이너, Photo Type 등 다양한 타입의 아이템 선택 상태를 관리합니다.
 * Set을 사용한 O(1) 조회로 성능을 최적화하고,
 * allItems 변경 시 stale selection을 자동으로 정리합니다.
 *
 * @template T - 선택 가능한 아이템의 타입 (number, string, enum 등)
 * @param allItems - 선택 가능한 모든 아이템 배열
 * @returns SelectionState<T> - 선택 상태 및 조작 함수들
 *
 * @example
 * // 숫자 타입 (컨테이너 ID)
 * const containerIds = [1, 2, 3, 4, 5];
 * const selection = useSelection(containerIds);
 *
 * // 문자열 타입 (Photo Type)
 * const photoTypes = ['SAMPLE_BALE', 'EMPTY', 'FULL_LOADED'];
 * const selection = useSelection(photoTypes);
 */
export const useSelection = <T>(allItems: T[]): SelectionState<T> => {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  // allItems가 변경되면 stale selection 정리
  useEffect(() => {
    const allItemsSet = new Set(allItems);
    setSelectedItems((prev) => {
      const filtered = prev.filter((item) => allItemsSet.has(item));
      // 변경된 경우에만 새 배열 반환 (불필요한 리렌더 방지)
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [allItems]);

  const addItem = useCallback((item: T) => {
    setSelectedItems((prev) => {
      if (prev.includes(item)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((item: T) => {
    setSelectedItems((prev) => prev.filter((i) => i !== item));
  }, []);

  const toggleItem = useCallback((item: T) => {
    setSelectedItems((prev) => {
      if (prev.includes(item)) {
        return prev.filter((i) => i !== item);
      }
      return [...prev, item];
    });
  }, []);

  const addAllItems = useCallback(() => {
    setSelectedItems(allItems);
  }, [allItems]);

  const removeAllItems = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const reset = useCallback(() => {
    setSelectedItems([]);
  }, []);

  // Set을 사용한 O(n) 복잡도 (기존 O(n²)에서 개선)
  const isAllSelected = useMemo(() => {
    if (allItems.length === 0) return false;
    const selectedSet = new Set(selectedItems);
    return allItems.every((item) => selectedSet.has(item));
  }, [allItems, selectedItems]);

  return {
    selectedItems,
    addItem,
    removeItem,
    addAllItems,
    removeAllItems,
    toggleItem,
    isAllSelected,
    reset,
  };
};
